import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { handleApiError } from "../../utils/errorHandler";
import { pushNotification } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider";
import { NotificationTypes } from "../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types";
import { Box, Button, Container, Skeleton, Typography } from "@mui/material";
import { PINNACLE_LOGO_URL } from "../../utils/constants";
import { getExamAttemptDetailsById } from "../../api/services/exam";
import { ExamAttemptDetails } from "../../api/interfaces/Exam";

const TakeExamSplash: React.FC = () => {
    const { examAttemptId } = useParams<{ examAttemptId: string }>();
    const navigate = useNavigate();
    const [attemptInfo, setAttemptInfo] = useState<ExamAttemptDetails | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        const run = async () => {
            try {
                setIsLoading(true);
                if (examAttemptId) {
                    const getAttemptInfo = await getExamAttemptDetailsById(examAttemptId);
                    setAttemptInfo(getAttemptInfo.data);
                }
            } catch (error) {
                handleApiError(error, {
                    notify: (msg) => {
                        pushNotification({
                            details: msg,
                            type: NotificationTypes.Error,
                        });
                    },
                });
            } finally {
                setIsLoading(false);
            }
        };
        run();
    }, []);

    const percentage =
        attemptInfo && attemptInfo.score !== null
            ? Math.round((attemptInfo.score / attemptInfo.numberOfQuestions) * 100)
            : null;

    return (
        <main
            style={{
                display: "flex",
                width: "100%",
                minHeight: "100vh",
                backgroundColor: "#f6f6f6",
                overflow: "hidden",
            }}
        >
            <Box
                component="section"
                sx={{
                    flexGrow: 1,
                    width: "100%",
                    overflowX: "hidden",
                    backgroundColor: "#EEF0FA",
                }}
            >
                <Container
                    maxWidth={false}
                    sx={{
                        mt: { xs: 4, sm: 6 },
                        mb: 4,
                        px: { xs: 2, sm: 3, lg: 15 },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: { xs: "column", md: "row" },
                            gap: 3,
                        }}
                    >

                        <Box
                            sx={{
                                flexGrow: 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: "calc(100vh - 120px)",
                            }}
                        >
                            <Box
                                sx={{
                                    width: "100%",
                                    maxWidth: 420,
                                    backgroundColor: "#fff",
                                    borderRadius: 2,
                                    boxShadow: "0px 4px 20px rgba(0,0,0,0.05)",
                                    p: 4,
                                    textAlign: "center",
                                }}
                            >
                                {/* Logo */}
                                <Box sx={{ mb: 2 }}>
                                    <img
                                        src={PINNACLE_LOGO_URL}
                                        alt="Preboard logo"
                                        style={{ height: 40 }}
                                    />
                                </Box>

                                {/* Attempt info */}
                                <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 400, color: "#818181", mb: 2, justifyItems: "center" }}
                                >
                                    {isLoading ? (
                                        <Skeleton width={160} height={20} />
                                    ) : attemptInfo ? (
                                        <>Attempt No: {attemptInfo.attemptNumber} of {attemptInfo.maxAttempts}</>
                                    ) : null}
                                </Typography>

                                {/* Message */}
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        mb: 2,
                                        fontWeight: 300,
                                        color: "#373737"
                                    }}
                                >
                                    Thank you for taking the exam.
                                    <br />
                                    Here is the result:
                                </Typography>

                                 <Typography variant="h4" sx={{ alignSelf: "center", fontWeight: 500, mb: 1, fontStyle: "italic" }}>
                                    {attemptInfo?.examName ?? (isLoading ? <Skeleton sx={{ alignSelf: "center" }} width={180} height={32} /> : "Exam Name")}
                                </Typography>

                                <Typography
                                    variant="h1"
                                    sx={{
                                        fontWeight: 700,
                                        color: "#5840BA",
                                        mb: 1,
                                    }}
                                >
                                    {isLoading ? (
                                        <Skeleton width={120} height={80} sx={{ mx: "auto" }} />
                                    ) : percentage !== null ? (
                                        `${percentage}%`
                                    ) : (
                                        "—"
                                    )}
                                </Typography>

                                <Typography
                                    variant="body1"
                                    sx={{ mb: 3, color: "#373737" }}
                                >
                                    {isLoading ? (
                                        <Skeleton width={260} height={24} sx={{ mx: "auto" }} />
                                    ) : attemptInfo ? (
                                        <>
                                            You got&nbsp;
                                            <span style={{ color: "#5840BA", fontWeight: 700 }}>
                                                {attemptInfo.score ?? "TBA"}
                                            </span>
                                            &nbsp;out of&nbsp;
                                            <span style={{ color: "#5840BA", fontWeight: 700 }}>
                                                {attemptInfo.numberOfQuestions}
                                            </span>
                                            &nbsp;items.
                                        </>
                                    ) : null}
                                </Typography>

                                <Button
                                    variant="outlined"
                                    sx={{
                                        mb: 4,
                                        textTransform: "none",
                                        borderRadius: 1,
                                        border: '1px solid #D8D8DD',
                                        color: "#373737",
                                        width: { xs: "100%", md: "50%" }
                                    }}
                                    onClick={() => navigate('/')}
                                >
                                    Back
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                </Container>
            </Box>
        </main>
    );
};

export default TakeExamSplash;
